import React, { type ReactNode } from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import FileIcon from '@atlaskit/icon/core/file';
import FilterIcon from '@atlaskit/icon/core/filter';
import PersonIcon from '@atlaskit/icon/core/person';
import { Box, Inline, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { ThirdPartyConfigsSync as ThirdPartyConfigs } from '@atlassian/rovo-connectors-config/configs-sync';
import type { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';

import type { SelectFilterOption } from '../../../common/constants/filters/select-filter/types';
import {
	type ProductConfig,
	type ProductKeys1P,
	products1P,
} from '../../../common/constants/products';
import { useAppContext } from '../../../common/ui/app-context';
import { FilterButton } from '../../../common/ui/quick-find/filter-button';
import { onAppFilterSelect as onAppFilterSelectEvent } from '../../../common/utils/quick-find/events/app-filter-selected';
import { AllAppsIcon } from '../../../controllers/all-apps-icon';
import {
	useQuickFindAnalytics,
	useQuickFindAttributes,
} from '../../../controllers/quick-find/analytics';
import { useQuickFindActions, useQuickFindQuery } from '../../../controllers/store/quick-find';
import { useSearchActions } from '../../../controllers/store/search';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { ContributorFilter } from '../filters/product-filters/contributor';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import { UniversalTypeFilter } from '../filters/product-filters/universal-type';

import { AppButton1P } from './app-button-1p';
import { AppButton3P } from './app-button-3p';
import { AppButtonFor3PApps } from './app-button-for-3p-apps';
import type { OnAppFilterSelectedCallback } from './filters-row-types';
import { messages } from './messages';
import { useFiltersRowAppSelector } from './utils';

/* eslint-disable @compiled/no-invalid-css-map */
const styles = cssMap({
	container: {
		marginBlock: token('space.300'),
		width: '100%',
		maxWidth: '914px',
		marginInline: 'auto',
		paddingInline: token('space.300'),
		flexWrap: 'wrap',
		'@media (min-width: 48rem)': {
			flexWrap: 'nowrap',
		},
	},
	icon: {
		color: token('color.text.subtlest'),
		paddingTop: token('space.050'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.050'),
	},
	defaultFilterButton: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.075'),
		color: token('color.text.subtle'),
		borderRadius: token('radius.small'),
		paddingInline: token('space.150'),
		paddingBlock: token('space.050'),
		width: 'max-content',
		minHeight: '32px', // As specified on Figma
		maxWidth: '165px', // As specified on Figma
		backgroundColor: token('color.background.neutral.subtle'),
		border: `${token('border.width')} solid ${token('color.border')}`,
		whiteSpace: 'nowrap',
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
	},
	filterButtonContent: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
});

export const FiltersRow = (): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { onNavigate, generateSearchUrl, queryParams } = useAppContext();
	const { setQuickFindOpen } = useQuickFindActions();
	const quickFindQuery = useQuickFindQuery();
	const { selectProductFilter, clearAllFilters } = useSearchActions();
	const { fireAnalyticsEvent } = useQuickFindAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useQuickFindAttributes();
	const { selectedProduct } = useFiltersRowAppSelector();

	const onAppFilterSelected: OnAppFilterSelectedCallback = (productKey, options, event) => {
		const newSelectedProduct = selectedProduct === productKey ? undefined : productKey;

		fireAnalyticsEvent(
			onAppFilterSelectEvent({
				actionSubjectId: productKey,
				action: newSelectedProduct === undefined ? 'unselected' : 'selected',
				attributes: {
					...commonAttributes,
					isPostAuthAction: options.isPostAuthAction,
					isRedirectedToFPS: options.redirectToFPS,
					source: 'inline',
				},
				nonPrivacySafeAttributes,
			}),
		);

		if (!options.redirectToFPS) {
			selectProductFilter(newSelectedProduct);
			return;
		}

		const onNavigateCallback = typeof onNavigate === 'function' ? onNavigate : onNavigate.callback;

		onNavigateCallback(
			generateSearchUrl({
				...queryParams,
				product: productKey,
				text: quickFindQuery,
			}),
			'push',
			event,
		);

		clearAllFilters();

		setQuickFindOpen(false);
	};

	return (
		<Inline space="space.150" alignBlock="center" alignInline="center" xcss={styles.container}>
			<Box xcss={styles.icon}>
				<FilterIcon label={formatMessage(messages.filterByIcon)} />
			</Box>
			<FilterSelects />
			<FilterApps onAppFilterSelected={onAppFilterSelected} />
		</Inline>
	);
};

const FilterSelects = () => {
	const { formatMessage } = useIntl();
	const { unselectProductFilter } = useSearchActions();

	// Deselect 'Confluence' when closing the contributor filter with no options selected
	// This should restore any 3P filters that were hidden while this filter was active
	const onFilterClose = (options: SelectFilterOption[], selectedOptions: SelectFilterOption[]) => {
		if (selectedOptions.length === 0) {
			unselectProductFilter();
		}
	};
	return (
		<>
			<ContributorFilter
				iconBefore={<PersonIcon label={formatMessage(messages.contributor)} />}
				iconAfter={<></>}
				onFilterClose={onFilterClose}
			/>

			<UniversalTypeFilter
				iconBefore={<FileIcon label={formatMessage(messages.type)} />}
				iconAfter={<></>}
			/>
		</>
	);
};

const FilterApps = ({
	onAppFilterSelected,
}: {
	onAppFilterSelected: OnAppFilterSelectedCallback;
}) => {
	const { apps3PToDisplay, apps1PToDisplay } = useFiltersRowAppSelector();

	return (
		<>
			{apps1PToDisplay.map((app: { productKey: ProductKeys1P; productConfig: ProductConfig }) => (
				<Box key={app.productKey}>
					<AppButton1P
						productConfig={app.productConfig}
						onAppFilterSelected={onAppFilterSelected}
						productKey={app.productKey}
					/>
				</Box>
			))}
			{apps3PToDisplay.map((app: { productKey: ProductKeys3P; productConfig: ProductConfig }) => (
				<Box key={app.productKey}>
					<AppButtonFor3PApps
						productKey={app.productKey}
						redirectToFPSWithProductSelected={() =>
							onAppFilterSelected(app.productKey, { redirectToFPS: true, isPostAuthAction: true })
						}
						trigger={({ triggerProps, userNeedsAuth }) => (
							<AppButton3P
								productConfig={app.productConfig}
								userNeedsAuth={userNeedsAuth}
								triggerProps={triggerProps}
								onAppFilterSelected={onAppFilterSelected}
								productKey={app.productKey}
							/>
						)}
					/>
				</Box>
			))}
			<AdditionalAppsButton />
		</>
	);
};

const AdditionalAppsButton = () => {
	const { formatMessage } = useIntl();
	const { fireAnalyticsEvent } = useQuickFindAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useQuickFindAttributes();
	const { onNavigate, generateSearchUrl, queryParams } = useAppContext();
	const quickFindQuery = useQuickFindQuery();
	const { clearAllFilters } = useSearchActions();
	const { setQuickFindOpen } = useQuickFindActions();

	const onClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		fireAnalyticsEvent(
			onAppFilterSelectEvent({
				actionSubjectId: 'more-apps-search',
				action: 'selected',
				attributes: {
					...commonAttributes,
					isPostAuthAction: false,
					isRedirectedToFPS: true,
					source: 'inline',
				},
				nonPrivacySafeAttributes,
			}),
		);

		const onNavigateCallback = typeof onNavigate === 'function' ? onNavigate : onNavigate.callback;

		onNavigateCallback(
			generateSearchUrl({
				...queryParams,
				text: quickFindQuery,
			}),
			'push',
			event,
		);

		clearAllFilters();

		setQuickFindOpen(false);
	};
	return (
		<FilterButton
			iconBefore={<AllAppsIcon />}
			contentFit={'default'}
			isSelected={false}
			spacing={'default'}
			tooltip={formatMessage(messages.moreAppsSearchTooltip)}
			onClick={onClick}
		>
			{`+${formatMessage(messages.more)}`}
		</FilterButton>
	);
};

export const FiltersRowLoading = (): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Inline space="space.150" alignBlock="center" alignInline="center" xcss={styles.container}>
			<Box xcss={styles.icon}>
				<FilterIcon label={formatMessage(messages.filterByIcon)} />
			</Box>
			<ContributorFilterLoading />
			<UniversalTypeFilterLoading />
			{/* Hard coded loaders for Team 25 */}
			{/* Sync with the logic in useFiltersRowAppSelector()  */}
			<ProductFilterLoading productKey="confluence" />
			<ProductFilterLoading productKey="drive" />
			<ProductFilterLoading productKey="teams" />
			<ProductMoreAppsButtonLoading />
		</Inline>
	);
};

const ContributorFilterLoading = () => {
	const { formatMessage } = useIntl();
	return (
		<Pressable xcss={styles.defaultFilterButton}>
			<PersonIcon label={formatMessage(messages.contributor)} />
			<Box xcss={styles.filterButtonContent}>{formatMessage(messages.contributor)}</Box>
		</Pressable>
	);
};

const UniversalTypeFilterLoading = () => {
	const { formatMessage } = useIntl();
	return (
		<Pressable xcss={styles.defaultFilterButton}>
			<FileIcon label={formatMessage(messages.type)} />
			<Box xcss={styles.filterButtonContent}>{formatMessage(messages.type)}</Box>
		</Pressable>
	);
};

const ProductFilterLoading = ({ productKey }: { productKey: ProductKeys1P | ProductKeys3P }) => {
	const config1P = products1P[productKey as ProductKeys1P];
	const config3P = ThirdPartyConfigs.get(productKey as ProductKeys3P);

	let displayName: ReactNode = '';
	let displayNameString: string = '';

	// 1P configs use config.Icon/IconBorderless, 3P configs use config.icon/iconBorderless
	let IconComponent;

	if (config1P) {
		// ProductConfig from products1P
		IconComponent = config1P.IconBorderless ?? config1P.Icon;
		displayName = config1P.displayName;
		displayNameString = String(displayName);
	} else if (config3P) {
		// ThirdPartyConfig from ThirdPartyConfigs
		IconComponent = config3P.iconBorderless ?? config3P.icon;
		displayName = config3P.displayName;
		displayNameString = String(displayName);
	} else {
		return null;
	}

	return (
		<Pressable xcss={styles.defaultFilterButton}>
			<IconComponent size="xsmall" label={displayNameString} />
			<Box xcss={styles.filterButtonContent}>{displayName}</Box>
		</Pressable>
	);
};

const ProductMoreAppsButtonLoading = () => {
	const { formatMessage } = useIntl();
	return (
		<Pressable xcss={styles.defaultFilterButton}>
			<AllAppsIcon />
			<Box xcss={styles.filterButtonContent}>{`+${formatMessage(messages.more)}`}</Box>
		</Pressable>
	);
};
